import React, { useState } from 'react';
import { Modal, Input, Button, Table, Typography, message, Space } from 'antd';
import { RobotOutlined, CheckCircleFilled } from '@ant-design/icons';
import { apiAuthHeaders } from '../services/apiAuth';


const { Text, Title } = Typography;

export interface GeneratedModule {
    name: string;
    description: string;
    questions?: any[];
}

export interface CurriculumResult {
    modules: GeneratedModule[];
    suggestedLink: string;
    estimatedEffortHours: number;
}

interface AICurriculumModalProps {
    open: boolean;
    onClose: () => void;
    onApply: (curriculum: CurriculumResult) => void;
    initialTopic?: string;
}

const AICurriculumModal: React.FC<AICurriculumModalProps> = ({ open, onClose, onApply, initialTopic }) => {
    const [topic, setTopic] = useState(initialTopic || '');
    const [extraPrompt, setExtraPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<CurriculumResult | null>(null);

    const executeGeneration = async (searchTopic: string) => {
        if (!searchTopic.trim()) {
            message.warning("Please enter a training topic first.");
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const response = await fetch('/api/generateCurriculum', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(await apiAuthHeaders()) },
                body: JSON.stringify({ topic: searchTopic, extraPrompt })
            });

            if (!response.ok) {
                throw new Error(`Server responded with status ${response.status}`);
            }

            const data = await response.json();

            if (data.curriculum && data.curriculum.modules) {
                setResult(data.curriculum);
                message.success("Successfully generated curriculum plan!");
            } else {
                throw new Error("Invalid response format from AI.");
            }

        } catch (error: any) {
            console.error("Curriculum generation failed:", error);
            message.error(error.message || "Failed to generate curriculum. Ensure your backend is running.");
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = () => executeGeneration(topic);

    React.useEffect(() => {
        if (open) {
            const initTopic = initialTopic || '';
            setTopic(initTopic);
            setExtraPrompt('');
            if (initTopic.trim()) {
                executeGeneration(initTopic);
            }
        } else {
            setResult(null);
            setLoading(false);
            setExtraPrompt('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleApply = () => {
        if (result) {
            onApply(result);
            setResult(null);
            setTopic('');
            setExtraPrompt('');
            onClose();
        }
    };

    const columns = [
        {
            title: 'Suggested Module Name',
            dataIndex: 'name',
            key: 'name',
            width: '35%',
            render: (text: string) => <Text strong>{text}</Text>
        },
        {
            title: 'Suggested Description',
            dataIndex: 'description',
            key: 'description'
        },
        {
            title: 'Quiz Questions',
            dataIndex: 'questions',
            key: 'questions',
            width: 150,
            render: (qs: any[]) => <Text type="success">✨ {qs?.length || 0} Questions</Text>
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    <RobotOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                    <span style={{ fontSize: 18 }}>AI Curriculum Designer</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={800}
            footer={null}
            destroyOnClose
            maskClosable={false}
        >
            <div style={{ marginBottom: 24, marginTop: 16 }}>
                <Text type="secondary">The AI will generate a suggested curriculum, including standard modules and a generic reference link, based on your training program name.</Text>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
                    <Text strong style={{ whiteSpace: 'nowrap' }}>Training Topic:</Text>
                    <Input
                        value={topic}
                        disabled
                        size="large"
                        style={{ backgroundColor: '#f5f5f5', flex: 1 }}
                    />
                    <Button
                        type="primary"
                        size="large"
                        icon={<RobotOutlined />}
                        loading={loading}
                        onClick={handleGenerate}
                        style={{ width: 170 }}
                    >
                        Regenerate Plan
                    </Button>
                </div>

                <div style={{ marginTop: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Additional Structure Guidelines (Optional):</Text>
                    <Input.TextArea
                        rows={2}
                        placeholder="e.g. Include a focus on React Hooks, make it 4 modules maximum, only intermediate/advanced level, etc."
                        value={extraPrompt}
                        onChange={(e) => setExtraPrompt(e.target.value)}
                        style={{ borderRadius: '8px' }}
                    />
                </div>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '40px 0', border: '1px dashed #d9d9d9', borderRadius: 8 }}>
                    <Title level={4} style={{ color: '#1890ff' }}>Thinking...</Title>
                    <Text type="secondary">The AI is structuring the curriculum content. This may take 10-20 seconds depending on the engine.</Text>
                </div>
            )}

            {result && !loading && (
                <div style={{ animation: 'fadeIn 0.5s ease-in-out' }}>
                    <div style={{ marginBottom: 16, display: 'flex', gap: '16px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, padding: '12px 16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>Suggested Reference Link:</Text>
                            <a href={result.suggestedLink} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{result.suggestedLink}</a>
                        </div>
                        <div style={{ padding: '12px 16px', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8, width: 170, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>Estimated Effort</Text>
                            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>{result.estimatedEffortHours || 0} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#8c8c8c' }}>hrs</span></Title>
                        </div>
                    </div>

                    <Table
                        dataSource={result.modules}
                        columns={columns}
                        rowKey="name"
                        pagination={false}
                        size="small"
                        bordered
                    />

                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Button onClick={onClose} style={{ marginRight: 12 }}>Cancel</Button>
                        <Button
                            type="primary"
                            icon={<CheckCircleFilled />}
                            onClick={handleApply}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Approve & Apply to Form
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default AICurriculumModal;
